import { useEffect, useState } from 'react';
import { Card, Button, Form, Input, Table, Tag, Typography, Space, message, Modal, Alert } from 'antd';
import { apiDelete, apiGet, apiPost, type ApiKeyCreated, type ApiKeySummary } from '../api/client';

export function ApiKeys() {
  const [keys, setKeys] = useState<ApiKeySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [form] = Form.useForm<{ label: string }>();

  async function load() {
    setLoading(true);
    try {
      const res = await apiGet<{ apiKeys: ApiKeySummary[] }>('/portal/api-keys');
      setKeys(res.apiKeys);
    } catch (err) {
      message.error(`Failed to load API keys: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onSubmit(values: { label: string }) {
    setSubmitting(true);
    try {
      const created = await apiPost<ApiKeyCreated, { label: string }>('/portal/api-keys', values);
      setCreatedKey(created);
      form.resetFields();
      await load();
    } catch (err) {
      message.error(`Failed to create API key: ${(err as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function onRevoke(id: string) {
    try {
      await apiDelete(`/portal/api-keys/${id}`);
      message.success('Key revoked');
      await load();
    } catch (err) {
      message.error(`Failed to revoke: ${(err as Error).message}`);
    }
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3}>API keys</Typography.Title>
      <Card title="Generate a new key">
        <Form form={form} layout="inline" onFinish={onSubmit}>
          <Form.Item label="Label" name="label" rules={[{ required: true }]}>
            <Input placeholder="frontend-prod" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Create key
          </Button>
        </Form>
      </Card>

      <Card>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={keys}
          pagination={false}
          columns={[
            { title: 'Label', dataIndex: 'label' },
            { title: 'Prefix', dataIndex: 'keyPrefix', render: (v: string) => <code>{v}…</code> },
            {
              title: 'Status',
              render: (_v, k) =>
                k.isRevoked ? <Tag color="red">Revoked</Tag> : <Tag color="green">Active</Tag>,
            },
            { title: 'Last used', dataIndex: 'lastUsedAt', render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—') },
            {
              title: '',
              render: (_v, k) =>
                k.isRevoked ? null : (
                  <Button danger size="small" onClick={() => onRevoke(k.id)}>
                    Revoke
                  </Button>
                ),
            },
          ]}
        />
      </Card>

      <Modal
        title="API key created"
        open={createdKey !== null}
        onOk={() => setCreatedKey(null)}
        onCancel={() => setCreatedKey(null)}
        okText="I've copied it"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {createdKey ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="Copy the key now — it will not be shown again."
            />
            <Input.TextArea readOnly value={createdKey.apiKey} rows={3} />
            <Typography.Text type="secondary">
              Pass it as the <code>X-API-KEY</code> header.
            </Typography.Text>
          </Space>
        ) : null}
      </Modal>
    </Space>
  );
}
